import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Camera,
  Check,
  ChevronDown,
  ExternalLink,
  Gamepad2,
  Globe,
  Loader2,
  Lock,
  Mail,
  MailOpen,
  Pencil,
  RotateCcw,
  Save,
  Settings,
  Shield,
  Trash2,
  Upload,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BattleNetIcon,
  EpicGamesIcon,
  RiotIcon,
  SteamIcon,
  XboxIcon,
} from "../../components/icons/BrandIcons";
import { getPlatformProfileUrl } from "../../components/icons/PlatformIcon";
import { ProfileSidebar } from "../../components/profile/ProfileSidebar";
import { SecuritySettings } from "../../components/profile/SecuritySettings";
import {
  Badge,
  Breadcrumbs,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  SegmentedControl,
  SettingsSection,
  Textarea,
} from "../../components/ui";
import { ImageCropModal } from "../../components/ui/ImageCropModal";
import { useImageCrop } from "../../hooks/useImageCrop";
import { useSession, type AppSessionUser } from "../../lib/auth-client";
import { statusMap } from "../../lib/status";
import { trpc } from "../../lib/trpc";
import { uploadFile } from "../../lib/uploadFile";
import type { CropData } from "../../lib/utils";
import { parseCropData } from "../../lib/utils";

type SettingsTab = "general" | "accounts" | "invites" | "security";

export const Route = createFileRoute("/app/settings")({
  staticData: { title: "Settings" },
  validateSearch: (search: Record<string, unknown>): { tab: SettingsTab } => {
    const tab = search.tab as string;
    if (tab === "general" || tab === "accounts" || tab === "invites" || tab === "security") {
      return { tab };
    }
    return { tab: "general" };
  },
  component: SettingsPage,
});

type PlatformId = "riot" | "steam" | "microsoft" | "epic" | "battlenet";

type PlatformConfig = {
  id: PlatformId;
  name: string;
  icon: (props: { className?: string }) => React.ReactNode;
  color: string;
  usernameLabel: string;
  usernamePlaceholder: string;
  hasDiscriminator: boolean;
  discriminatorPlaceholder?: string;
  idLabel: string;
  idPlaceholder: string;
};

const platforms: PlatformConfig[] = [
  {
    id: "steam",
    name: "Steam",
    icon: SteamIcon,
    color: "text-surface-200",
    usernameLabel: "Steam Username",
    usernamePlaceholder: "Your Steam display name",
    hasDiscriminator: false,
    idLabel: "Steam ID (optional)",
    idPlaceholder: "e.g. 76561198...",
  },
  {
    id: "epic",
    name: "Epic Games",
    icon: EpicGamesIcon,
    color: "text-surface-200",
    usernameLabel: "Epic Display Name",
    usernamePlaceholder: "Your Epic username",
    hasDiscriminator: false,
    idLabel: "Epic Account ID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
  },
  {
    id: "microsoft",
    name: "Xbox / Microsoft",
    icon: XboxIcon,
    color: "text-green-400",
    usernameLabel: "Gamertag",
    usernamePlaceholder: "Your Xbox Gamertag",
    hasDiscriminator: false,
    idLabel: "XUID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
  },
  {
    id: "riot",
    name: "Riot Games",
    icon: RiotIcon,
    color: "text-red-400",
    usernameLabel: "Riot ID",
    usernamePlaceholder: "Player",
    hasDiscriminator: true,
    discriminatorPlaceholder: "NA1",
    idLabel: "PUUID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
  },
  {
    id: "battlenet",
    name: "Battle.net",
    icon: BattleNetIcon,
    color: "text-sky-400",
    usernameLabel: "BattleTag",
    usernamePlaceholder: "Player",
    hasDiscriminator: true,
    discriminatorPlaceholder: "1234",
    idLabel: "Battle.net ID (optional)",
    idPlaceholder: "Leave blank if you don't know it",
  },
];

const settingsTabs = [
  { id: "general" as const, label: "General", icon: Settings },
  { id: "accounts" as const, label: "Game Accounts", icon: Gamepad2 },
  { id: "security" as const, label: "Security", icon: Shield },
  { id: "invites" as const, label: "Invites", icon: Mail },
];

function SettingsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user as AppSessionUser | undefined;
  const { data: authProviders } = trpc.user.authProviders.useQuery();

  // Profile save state — lifted so the button can live in the header
  const [profileSaving, setProfileSaving] = useState(false);
  const profileSaveRef = useRef<(() => void) | null>(null);

  const showSave = tab === "general";

  const setActiveTab = (tabId: SettingsTab) => {
    void navigate({
      to: "/app/settings",
      search: { tab: tabId },
      replace: true,
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            {
              label: "My Profile",
              to: "/app/users/$slug",
              params: { slug: user?.slug ?? user?.id ?? "" },
              replace: true,
            },
            { label: "Settings" },
          ]}
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Profile Settings</h1>
            <p className="text-sm text-surface-400">
              Configure your profile, accounts, and preferences.
            </p>
          </div>
          {showSave && (
            <Button
              variant="primary"
              size="md"
              leftIcon={
                profileSaving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )
              }
              disabled={profileSaving}
              onClick={() => profileSaveRef.current?.()}
            >
              {profileSaving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="lg:hidden overflow-x-auto scrollbar-none -mx-4 px-4">
        <div className="flex gap-1 min-w-max pb-1">
          {settingsTabs.map((item) => {
            const Icon = item.icon;
            const isActive = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveTab(item.id)}
                className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? "bg-brand-500/10 text-brand-400 shadow-brand-glow-sm"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                }`}
              >
                <Icon className="size-3.5" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: sidebar + content */}
      <div className="flex gap-6">
        <nav className="hidden lg:block w-56 shrink-0 border-r border-surface-700/30">
          <div className="sticky top-6 space-y-1 pr-4">
            {settingsTabs.map((item) => {
              const Icon = item.icon;
              const isActive = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-brand-500/8 text-white"
                      : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/40"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center size-8 rounded-lg transition-all duration-200 shrink-0 ${
                      isActive
                        ? "bg-brand-500/15 text-brand-400"
                        : "bg-surface-800/50 text-surface-500 group-hover:text-surface-300 group-hover:bg-surface-800"
                    }`}
                  >
                    <Icon className="size-4" />
                  </div>
                  <span className={`text-sm font-semibold ${isActive ? "text-white" : ""}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="flex-1 min-w-0">
          <SettingsSectionContent
            authProviders={authProviders}
            tab={tab}
            onProfileSavingChange={setProfileSaving}
            profileSaveRef={profileSaveRef}
          />
        </div>
      </div>
    </div>
  );
}

function SettingsSectionContent({
  authProviders,
  tab,
  onProfileSavingChange,
  profileSaveRef,
}: {
  authProviders:
    | {
        hasCredentialProvider: boolean;
        hasDiscordProvider: boolean;
        hasGoogleProvider: boolean;
      }
    | undefined;
  tab: SettingsTab;
  onProfileSavingChange: (saving: boolean) => void;
  profileSaveRef: React.MutableRefObject<(() => void) | null>;
}) {
  switch (tab) {
    case "general":
      return <ProfileSettings onSavingChange={onProfileSavingChange} saveRef={profileSaveRef} />;
    case "accounts":
      return <GameAccountSettings />;
    case "security":
      return (
        <SecuritySettings
          hasCredentialProvider={authProviders?.hasCredentialProvider}
          hasDiscordProvider={authProviders?.hasDiscordProvider}
          hasGoogleProvider={authProviders?.hasGoogleProvider}
        />
      );
    case "invites":
      return <PendingInvitesSettings />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Profile Tab
// ---------------------------------------------------------------------------

function ProfileSettings({
  onSavingChange,
  saveRef,
}: {
  onSavingChange: (saving: boolean) => void;
  saveRef: React.MutableRefObject<(() => void) | null>;
}) {
  const { data: session, refetch: refetchSession } = useSession();
  const user = session?.user as AppSessionUser | undefined;
  const { data: accounts } = trpc.linkedAccount.list.useQuery();
  const [displayName, setDisplayName] = useState(user?.name ?? "");
  const [bio, setBio] = useState(user?.bio ?? "");
  const userStatus = user?.status ?? "offline";
  const status = statusMap[userStatus] ?? statusMap.offline;

  // Avatar form state
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [pendingAvatarBlob, setPendingAvatarBlob] = useState<Blob | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarCropData, setAvatarCropData] = useState<CropData | null>(null);

  // Banner form state (needed for save handler)
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [pendingBannerSourceFile, setPendingBannerSourceFile] = useState<File | null>(null);
  const [pendingBannerBlob, setPendingBannerBlob] = useState<Blob | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerCropData, setBannerCropData] = useState<CropData | null>(() =>
    parseCropData(user?.bannerCropData),
  );
  const [, setIsSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(user?.isPublic ?? true);

  // Crop interaction via shared hook
  const { getSrc, handleFileSelect, handleImageClick, cropModalProps } = useImageCrop(
    {
      avatar: {
        maxFileSize: 2 * 1024 * 1024,
        outputWidth: 256,
        outputHeight: 256,
        aspect: 1,
        shape: "round",
      },
      banner: {
        maxFileSize: 5 * 1024 * 1024,
        outputWidth: 680,
        outputHeight: 340,
        aspect: 2,
      },
    },
    {
      avatar: {
        inputRef: avatarInputRef,
        preview: avatarPreview,
        displayUrl: (user?.image as string) || null,
        sourceUrl: null,
        cropData: avatarCropData,
        setPendingSourceFile: () => {},
        setPendingBlob: setPendingAvatarBlob,
        setPreview: setAvatarPreview,
        setCropData: setAvatarCropData,
      },
      banner: {
        inputRef: bannerInputRef,
        preview: bannerPreview,
        displayUrl: (user?.bannerUrl as string) || null,
        sourceUrl: (user?.bannerSourceUrl as string) || null,
        cropData: bannerCropData,
        setPendingSourceFile: setPendingBannerSourceFile,
        setPendingBlob: setPendingBannerBlob,
        setPreview: setBannerPreview,
        setCropData: setBannerCropData,
      },
    },
  );

  const avatarSrc = getSrc("avatar");
  const bannerSrc = getSrc("banner");

  // Sync state when user data loads
  useEffect(() => {
    if (user) {
      setDisplayName(user.name ?? "");
      setBio(user.bio ?? "");
      setBannerCropData(parseCropData(user.bannerCropData));
      setIsPublic(user.isPublic ?? true);
    }
  }, [user?.bannerCropData, user?.bio, user?.isPublic, user?.name]);

  const updateProfile = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      void refetchSession({ query: { disableCookieCache: true } });
    },
  });

  const updatePrivacy = trpc.user.updatePrivacy.useMutation({
    onSuccess: () => {
      void refetchSession({ query: { disableCookieCache: true } });
    },
  });

  const doUpload = async (
    file: Blob | File,
    category: "user-avatar" | "user-banner" | "user-banner-source",
  ): Promise<string> => {
    if (!user) throw new Error("User not found");
    const { publicUrl } = await uploadFile(file, category, user.id);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  const serverBannerCrop = parseCropData(user?.bannerCropData);

  const displayNameChanged = displayName.trim() !== (user?.name ?? "");
  const bioChanged = (bio || "") !== (user?.bio ?? "");
  const privacyChanged = isPublic !== (user?.isPublic ?? true);

  const handleSave = useCallback(async () => {
    if (!user) return;

    setIsSaving(true);
    onSavingChange(true);
    try {
      const updates: Record<string, unknown> = {};

      if (displayNameChanged) updates.name = displayName.trim();
      if (bioChanged) updates.bio = bio || undefined;

      // Upload cropped avatar
      if (pendingAvatarBlob) {
        updates.avatarUrl = await doUpload(pendingAvatarBlob, "user-avatar");
      }

      // Upload banner source image (original uncropped)
      if (pendingBannerSourceFile) {
        updates.bannerSourceUrl = await doUpload(pendingBannerSourceFile, "user-banner-source");
      }

      // Upload cropped display banner
      if (pendingBannerBlob) {
        updates.bannerUrl = await doUpload(pendingBannerBlob, "user-banner");
      }

      // Crop data
      if (JSON.stringify(bannerCropData) !== JSON.stringify(serverBannerCrop)) {
        updates.bannerCropData = bannerCropData;
      }

      if (Object.keys(updates).length > 0) {
        await updateProfile.mutateAsync(updates as Parameters<typeof updateProfile.mutate>[0]);
        setPendingAvatarBlob(null);
        setPendingBannerSourceFile(null);
        setPendingBannerBlob(null);
      }

      if (privacyChanged) {
        await updatePrivacy.mutateAsync({ isPublic });
      }

      toast.success("Profile updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
      onSavingChange(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- derived values (bioChanged, privacyChanged) included via their source deps
  }, [
    user,
    displayName,
    bio,
    pendingAvatarBlob,
    pendingBannerSourceFile,
    pendingBannerBlob,
    bannerCropData,
    serverBannerCrop,
    isPublic,
    onSavingChange,
  ]);

  // Register save handler with parent
  useEffect(() => {
    saveRef.current = handleSave;
    return () => {
      saveRef.current = null;
    };
  }, [handleSave, saveRef]);

  const charCount = bio.length;
  const charPercent = (charCount / 500) * 100;

  return (
    <SettingsSection
      title="General"
      icon={Settings}
      className="animate-fade-in"
      action={
        user ? (
          <SegmentedControl
            size="sm"
            value={isPublic ? "public" : "private"}
            onChange={(v) => setIsPublic(v === "public")}
            options={[
              {
                value: "public",
                label: (
                  <span className="flex items-center gap-1.5">
                    <Globe className="size-3" />
                    Public
                  </span>
                ),
              },
              {
                value: "private",
                label: (
                  <span className="flex items-center gap-1.5">
                    <Lock className="size-3" />
                    Private
                  </span>
                ),
              },
            ]}
          />
        ) : undefined
      }
    >
      <div className="space-y-8">
        <div className="space-y-4">
          <Input
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={255}
            placeholder="What should other players call you?"
          />

          <div className="space-y-1.5">
            <Textarea
              label="About"
              placeholder="Tell others about yourself..."
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
            />
            <div className="flex items-center justify-between">
              <p className="text-2xs text-surface-500">
                {isPublic
                  ? "Visible on your public profile"
                  : "Profile is private — only basic info is shown to others"}
              </p>
              <span
                className={`text-2xs tabular-nums ${charPercent > 90 ? "text-error-400" : "text-surface-500"}`}
              >
                {charCount}/500
              </span>
            </div>
          </div>
        </div>

        {/* Avatar */}
        <div>
          <span className="text-sm font-medium text-content-secondary">Avatar</span>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileSelect("avatar")}
            className="hidden"
          />
          <div className="mt-2 flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleImageClick("avatar")}
              className="group relative size-20 rounded-full overflow-hidden
                bg-surface-800 border-2 border-surface-700/40
                hover:border-brand-500/40 transition-all duration-200"
            >
              {avatarSrc ? (
                <img src={avatarSrc} alt="Avatar" className="size-full object-cover" />
              ) : (
                <div
                  className="size-full flex items-center justify-center
                  bg-surface-700 text-surface-400 text-xl font-bold"
                >
                  {user?.name?.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div
                className="absolute inset-0 bg-black/50 opacity-0
                group-hover:opacity-100 transition-opacity flex items-center
                justify-center"
              >
                <Camera className="size-5 text-white" />
              </div>
            </button>
            <div className="space-y-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="!px-2.5 !py-1 !text-xs"
                leftIcon={
                  avatarSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                }
                onClick={() => avatarInputRef.current?.click()}
              >
                {avatarSrc ? "Replace" : "Upload"}
              </Button>
              <p className="text-2xs text-surface-500">256 × 256px. Max 2 MB.</p>
            </div>
          </div>
        </div>

        {/* Banner */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-content-secondary">Banner</span>
          </div>

          <input
            ref={bannerInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileSelect("banner")}
            className="hidden"
          />

          {/* Profile Preview */}
          <div className="flex items-center justify-between">
            <span className="text-2xs font-medium text-surface-500 uppercase tracking-wider">
              Profile Preview
            </span>
            <div className="flex items-center gap-1.5">
              {bannerSrc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2.5 !py-1 !text-xs"
                  leftIcon={<Camera className="size-3" />}
                  onClick={() => handleImageClick("banner")}
                >
                  Adjust
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="!px-2.5 !py-1 !text-xs"
                leftIcon={
                  bannerSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                }
                onClick={() => bannerInputRef.current?.click()}
              >
                {bannerSrc ? "Replace" : "Upload"}
              </Button>
            </div>
          </div>
          <div className="rounded-xl border border-surface-700/40 bg-surface-900 p-8 flex justify-center">
            <div className="w-[340px]">
              {user && (
                <ProfileSidebar
                  user={{
                    ...user,
                    slug: user.slug ?? user.id,
                    avatarUrl: avatarSrc ?? user.image,
                    bannerUrl: bannerSrc ?? user.bannerUrl ?? undefined,
                    status: user.status ?? "offline",
                    isPublic: user.isPublic ?? true,
                  }}
                  status={status}
                  accounts={accounts}
                  bioOverride={bio}
                  onClick={() => handleImageClick("banner")}
                />
              )}
            </div>
          </div>
          <p className="text-2xs text-surface-500 pl-1">
            {bannerSrc
              ? "Recommended: 680 × 340px. Max 5 MB."
              : "Click the preview to upload a banner. 680 × 340px recommended."}
          </p>
        </div>

        {/* Crop Modal */}
        {cropModalProps && <ImageCropModal {...cropModalProps} />}
      </div>
    </SettingsSection>
  );
}

// ---------------------------------------------------------------------------
// Game Accounts Tab
// ---------------------------------------------------------------------------

function GameAccountSettings() {
  const { data: accounts, isLoading } = trpc.linkedAccount.list.useQuery();
  const utils = trpc.useUtils();
  const [expandedPlatform, setExpandedPlatform] = useState<PlatformId | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const linkedPlatforms = new Set(accounts?.map((a) => a.platform) ?? []);
  const linkedCount = linkedPlatforms.size;

  const unlinkMutation = trpc.linkedAccount.unlink.useMutation({
    onSuccess: () => {
      toast.success("Account unlinked");
      void utils.linkedAccount.list.invalidate();
      setUnlinkTarget(null);
    },
    onError: () => toast.error("Failed to unlink account"),
  });

  return (
    <SettingsSection
      title="Game Accounts"
      icon={Gamepad2}
      className="animate-fade-in"
      action={
        <Badge color="brand" variant="subtle" size="sm">
          {linkedCount}/{platforms.length} linked
        </Badge>
      }
    >
      <div className="space-y-5">
        <div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[72px] rounded-xl bg-surface-700/20 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-2.5">
              {platforms.map((platform) => {
                const Icon = platform.icon;
                const isLinked = linkedPlatforms.has(platform.id);
                const linkedAccount = accounts?.find((a) => a.platform === platform.id);
                const isExpanded = expandedPlatform === platform.id && !isLinked;
                const isEditing = editingAccountId === linkedAccount?.id;
                const profileUrl = linkedAccount
                  ? getPlatformProfileUrl(
                      platform.id,
                      linkedAccount.platformUserId,
                      linkedAccount.platformUsername,
                    )
                  : null;

                return (
                  <div
                    key={platform.id}
                    className={`group rounded-xl border transition-all duration-200 overflow-hidden ${
                      isLinked
                        ? "border-brand-500/20 bg-brand-500/[0.03]"
                        : isExpanded
                          ? "border-surface-600 bg-surface-800/50"
                          : "border-surface-700/30 bg-surface-875 hover:border-surface-600/60 hover:bg-surface-800/30"
                    }`}
                  >
                    {/* Header row */}
                    <div className="flex items-center gap-4 px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => {
                          if (!isLinked) {
                            setExpandedPlatform(isExpanded ? null : platform.id);
                          }
                        }}
                        className="flex items-center gap-3.5 flex-1 min-w-0 text-left"
                      >
                        <div
                          className={`flex items-center justify-center size-10 rounded-lg transition-all duration-200 shrink-0 ${
                            isLinked
                              ? "bg-brand-500/12 text-brand-400"
                              : `bg-surface-700/40 ${platform.color}`
                          }`}
                        >
                          <Icon className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`font-semibold text-sm ${isLinked ? "text-white" : "text-surface-300"}`}
                          >
                            {platform.name}
                          </p>
                          {isLinked && linkedAccount ? (
                            <p className="text-xs text-brand-400/70 mt-0.5 truncate">
                              {linkedAccount.platformUsername}
                              {linkedAccount.platformUsernameDiscriminator && (
                                <span className="text-brand-400/40">
                                  #{linkedAccount.platformUsernameDiscriminator}
                                </span>
                              )}
                            </p>
                          ) : (
                            <p className="text-2xs text-surface-500 mt-0.5">Not connected</p>
                          )}
                        </div>
                      </button>

                      {isLinked && linkedAccount ? (
                        <div className="flex items-center gap-1.5 shrink-0">
                          {linkedAccount.verified ? (
                            <Badge color="green" variant="subtle" size="sm">
                              <Shield className="size-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge color="gray" variant="subtle" size="sm">
                              Manual
                            </Badge>
                          )}
                          {profileUrl && (
                            <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="!px-2">
                                <ExternalLink className="size-3.5 text-surface-400" />
                              </Button>
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!px-2"
                            onClick={() => setEditingAccountId(isEditing ? null : linkedAccount.id)}
                          >
                            <Pencil className="size-3.5 text-surface-400" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!px-2"
                            onClick={() =>
                              setUnlinkTarget({
                                id: linkedAccount.id,
                                name: platform.name,
                              })
                            }
                          >
                            <Trash2 className="size-3.5 text-error-400" />
                          </Button>
                        </div>
                      ) : (
                        <ChevronDown
                          className={`size-4 text-surface-500 transition-transform duration-200 shrink-0 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </div>

                    {/* Edit username form (for linked accounts) */}
                    {isLinked && isEditing && linkedAccount && (
                      <div className="px-4 pb-4 pt-1 border-t border-surface-700/30">
                        <EditUsernameForm
                          accountId={linkedAccount.id}
                          platform={platform}
                          currentUsername={linkedAccount.platformUsername}
                          currentDiscriminator={linkedAccount.platformUsernameDiscriminator ?? ""}
                          onDone={() => setEditingAccountId(null)}
                        />
                      </div>
                    )}

                    {/* Expanded: link form (for unlinked accounts) */}
                    {isExpanded && (
                      <ExpandedLinkSection
                        platform={platform}
                        onSuccess={() => {
                          setExpandedPlatform(null);
                          void utils.linkedAccount.list.invalidate();
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Unlink confirmation modal */}
        {unlinkTarget && (
          <Modal open={true} onClose={() => setUnlinkTarget(null)} className="!max-w-sm">
            <div className="px-6 pt-6 pb-1 flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-error-500/10 border border-error-500/20">
                <Trash2 className="size-5 text-error-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Unlink Account</h2>
                <p className="text-sm text-surface-400">This cannot be undone</p>
              </div>
            </div>
            <ModalBody>
              <p className="text-sm text-surface-300">
                Are you sure you want to unlink your{" "}
                <span className="font-semibold text-white">{unlinkTarget.name}</span> account?
                You'll need to re-link it if you want to use it again.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                onClick={() => setUnlinkTarget(null)}
                disabled={unlinkMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="!bg-error-500 hover:!bg-error-600"
                disabled={unlinkMutation.isPending}
                leftIcon={
                  unlinkMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Trash2 className="size-4" />
                  )
                }
                onClick={() => unlinkMutation.mutate({ accountId: unlinkTarget.id })}
              >
                {unlinkMutation.isPending ? "Unlinking..." : "Unlink"}
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </div>
    </SettingsSection>
  );
}

function ExpandedLinkSection({
  platform,
  onSuccess,
}: {
  platform: PlatformConfig;
  onSuccess: () => void;
}) {
  const [showManualForm, setShowManualForm] = useState(false);

  const handleOAuthClick = () => {
    toast("OAuth linking coming soon!", {
      description: `${platform.name} sign-in will be available in a future update.`,
      icon: <Zap className="size-4 text-brand-400" />,
    });
  };

  return (
    <div className="px-4 pb-4 pt-1 border-t border-surface-700/30 animate-fade-in">
      {!showManualForm ? (
        <div className="space-y-4 pt-3">
          <Button
            variant="primary"
            className="w-full"
            leftIcon={<ExternalLink className="size-4" />}
            onClick={handleOAuthClick}
          >
            Link with {platform.name}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-surface-700/50" />
            <span className="text-[11px] uppercase tracking-wider text-surface-600 font-medium">
              or
            </span>
            <div className="flex-1 h-px bg-surface-700/50" />
          </div>
          <Button
            variant="ghost"
            className="w-full"
            size="sm"
            onClick={() => setShowManualForm(true)}
          >
            Enter details manually
          </Button>
        </div>
      ) : (
        <div className="pt-3">
          <button
            type="button"
            onClick={() => setShowManualForm(false)}
            className="text-xs font-medium text-surface-500 hover:text-surface-300 transition-colors mb-3"
          >
            &larr; Back
          </button>
          <ManualLinkForm platform={platform} onSuccess={onSuccess} />
        </div>
      )}
    </div>
  );
}

function ManualLinkForm({
  platform,
  onSuccess,
}: {
  platform: PlatformConfig;
  onSuccess: () => void;
}) {
  const [username, setUsername] = useState("");
  const [discriminator, setDiscriminator] = useState("");
  const [userId, setUserId] = useState("");

  const linkMutation = trpc.linkedAccount.linkManual.useMutation({
    onSuccess: () => {
      toast.success(`${platform.name} account linked!`);
      onSuccess();
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT") {
        toast.error(`You already have a ${platform.name} account linked`);
      } else {
        toast.error(`Failed to link ${platform.name} account`);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    linkMutation.mutate({
      platform: platform.id,
      platformUserId: userId.trim() || username.trim(),
      platformUsername: username.trim(),
      platformUsernameDiscriminator: discriminator.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {platform.hasDiscriminator ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={platform.usernameLabel}
              placeholder={platform.usernamePlaceholder}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className="w-28">
            <Input
              label="Tag"
              placeholder={platform.discriminatorPlaceholder}
              value={discriminator}
              onChange={(e) => setDiscriminator(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <Input
          label={platform.usernameLabel}
          placeholder={platform.usernamePlaceholder}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      )}
      <Input
        label={platform.idLabel}
        placeholder={platform.idPlaceholder}
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />

      {linkMutation.isError && (
        <p className="text-sm text-error-400">{linkMutation.error.message}</p>
      )}

      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!username.trim() || linkMutation.isPending}
          leftIcon={
            linkMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : undefined
          }
        >
          {linkMutation.isPending ? "Linking..." : "Link Account"}
        </Button>
      </div>
    </form>
  );
}

function EditUsernameForm({
  accountId,
  platform,
  currentUsername,
  currentDiscriminator,
  onDone,
}: {
  accountId: string;
  platform: PlatformConfig;
  currentUsername: string;
  currentDiscriminator: string;
  onDone: () => void;
}) {
  const utils = trpc.useUtils();
  const [username, setUsername] = useState(currentUsername);
  const [discriminator, setDiscriminator] = useState(currentDiscriminator);

  const updateMutation = trpc.linkedAccount.updateUsername.useMutation({
    onSuccess: () => {
      toast.success("Username updated");
      void utils.linkedAccount.list.invalidate();
      onDone();
    },
    onError: () => toast.error("Failed to update username"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    updateMutation.mutate({
      accountId,
      platformUsername: username.trim(),
      platformUsernameDiscriminator: discriminator.trim() || null,
    });
  };

  const hasChanges =
    username.trim() !== currentUsername || discriminator.trim() !== currentDiscriminator;

  return (
    <form onSubmit={handleSubmit} className="pt-3 space-y-3">
      <p className="text-xs text-surface-500 font-semibold uppercase tracking-widest">
        Edit Username
      </p>
      {platform.hasDiscriminator ? (
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={platform.usernameLabel}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className="w-28">
            <Input
              label="Tag"
              placeholder={platform.discriminatorPlaceholder}
              value={discriminator}
              onChange={(e) => setDiscriminator(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <Input
          label={platform.usernameLabel}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
        />
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!username.trim() || !hasChanges || updateMutation.isPending}
          leftIcon={
            updateMutation.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )
          }
        >
          {updateMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Pending Invites Tab
// ---------------------------------------------------------------------------

function PendingInvitesSettings() {
  const utils = trpc.useUtils();
  const { data: invites, isLoading } = trpc.clubInvite.listPending.useQuery();

  const acceptMutation = trpc.clubInvite.accept.useMutation({
    onSuccess: () => {
      toast.success("Invite accepted!");
      void utils.clubInvite.listPending.invalidate();
      void utils.club.getMyClubs.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to accept invite"),
  });

  const declineMutation = trpc.clubInvite.decline.useMutation({
    onSuccess: () => {
      toast.success("Invite declined");
      void utils.clubInvite.listPending.invalidate();
    },
    onError: () => toast.error("Failed to decline invite"),
  });

  return (
    <SettingsSection
      title="Invites"
      icon={Mail}
      className="animate-fade-in"
      action={
        invites && invites.length > 0 ? (
          <Badge color="brand" variant="subtle" size="sm">
            {invites.length} pending
          </Badge>
        ) : undefined
      }
    >
      <div>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-surface-700/20 animate-pulse" />
            ))}
          </div>
        ) : invites && invites.length > 0 ? (
          <div className="space-y-2.5">
            {invites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center gap-4 p-4 rounded-xl bg-surface-875 border border-surface-700/30 hover:border-surface-600/50 transition-colors"
              >
                <div className="size-12 rounded-xl bg-surface-700/40 flex items-center justify-center shrink-0 overflow-hidden">
                  {invite.clubAvatarUrl ? (
                    <img src={invite.clubAvatarUrl} alt="" className="size-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-surface-400">
                      {invite.clubName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{invite.clubName}</p>
                  <p className="text-xs text-surface-400 mt-0.5">
                    Invited by <span className="text-surface-300">{invite.inviterUsername}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-error-400 hover:!bg-error-500/10"
                    disabled={declineMutation.isPending}
                    onClick={() => declineMutation.mutate({ inviteId: invite.id })}
                  >
                    <X className="size-4" />
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={acceptMutation.isPending}
                    leftIcon={
                      acceptMutation.isPending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Check className="size-3.5" />
                      )
                    }
                    onClick={() => acceptMutation.mutate({ inviteId: invite.id })}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="flex items-center justify-center size-14 rounded-2xl bg-surface-800/80 border border-surface-700/40 mb-4">
              <MailOpen className="size-6 text-surface-500" />
            </div>
            <p className="text-sm font-semibold text-surface-300">No pending invites</p>
            <p className="text-xs text-surface-500 mt-1 max-w-xs">
              When someone invites you to a club, it will appear here for you to accept or decline.
            </p>
          </div>
        )}
      </div>
    </SettingsSection>
  );
}
